import { MenuItem } from '@app/types';
import { MenuItemFavoritedOfBag, MenuItemWithDetails, MenuItemWithDetailsOfBag } from '@app/types/menu-item';
import { calculateTotalItemsPrice } from '@app/utils/calculus';
import { isMenuItemsBagEquals, MenuItemFactory } from '@app/utils/menu';
import { SessionStorage } from '@app/utils/storage';
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useReducer } from 'react';
import { useRef } from 'react';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type MenuItemOfBag = MenuItemWithDetailsOfBag | MenuItemFavoritedOfBag;

type MenuItemExtraData = {
  observations: string;
  amount: number;
};

type BagContextData = {
  items: MenuItemOfBag[];
  addItem: (item: MenuItemWithDetails, extra: MenuItemExtraData) => void;
  deleteItemByIndex: (index: number) => void;
  updateItem: (bagItemIdForEdit: string, newDataItem: MenuItemOfBag, extra: MenuItemExtraData) => void;
  clearItems: () => void;
  totalItemsPrice: number;
};

const BagContext = createContext({} as BagContextData);

type Props = {
  cliqueiAcheiId: string;
  children: ReactNode;
};

const initialState: MenuItemOfBag[] = [];

export default function BagContextProvider({ children, cliqueiAcheiId }: Props) {
  const [items, dispatch] = useReducer(itemsReducer, initialState);

  const setItems = useCallback((newItems: MenuItemOfBag[]) => {
    dispatch({
      type: 'SET_ITEMS',
      payload: { items: newItems },
    });
  }, []);

  const loadedItemsFromStorage = useRef(false);
  useEffect(() => {
    const itemsStored = BagStorage.get(cliqueiAcheiId);
    loadedItemsFromStorage.current = true;
    setItems(itemsStored);
  }, [cliqueiAcheiId]);

  useEffect(() => {
    if (!loadedItemsFromStorage.current) return;
    BagStorage.save(cliqueiAcheiId, items);
  }, [cliqueiAcheiId, items]);

  const deleteItemByIndex = useCallback((index: number) => {
    dispatch({
      type: 'DELETE_ITEM_BY_INDEX',
      payload: { indexToDelete: index },
    });
  }, []);

  const updateItem = useCallback(
    (bagItemIdForEdit: string, newDataItem: MenuItemOfBag, extra: MenuItemExtraData) => {
      const itemAlreadyExists = items.find(
        (item) => isMenuItemsBagEquals(item, { ...newDataItem, ...extra }) && item.bagItemId !== bagItemIdForEdit
      );

      if (itemAlreadyExists) deleteItemBeenEdited();

      const bagItemIdFinal = itemAlreadyExists?.bagItemId ?? bagItemIdForEdit;
      const menuItemOfBag = MenuItemFactory.createMenuItemWithDetailsOfBag({
        menuItem: newDataItem,
        amount: extra.amount + (itemAlreadyExists?.amount ?? 0),
        observations: extra.observations,
        bagItemId: bagItemIdFinal,
      });

      dispatch({
        type: 'UPDATE_ITEM',
        payload: { bagItemIdForEdit: bagItemIdFinal, newData: menuItemOfBag },
      });

      function deleteItemBeenEdited() {
        const bagItemForEditIndexToDelete = items.findIndex((item) => item.bagItemId === bagItemIdForEdit);
        if (bagItemForEditIndexToDelete !== -1) deleteItemByIndex(bagItemForEditIndexToDelete);
      }
    },
    [items]
  );

  const addItem = useCallback(
    (menuItemData: MenuItemWithDetails, extra: MenuItemExtraData) => {
      const itemAlreadyExists = items.find((item) =>
        isMenuItemsBagEquals(item, { ...menuItemData, ...extra, bagItemId: 'new' })
      );

      if (itemAlreadyExists) {
        updateItemThatAlreadyExists();
        return;
      }

      const menuItemOfBag = MenuItemFactory.createMenuItemWithDetailsOfBag({
        menuItem: menuItemData,
        amount: extra.amount,
        observations: extra.observations,
        bagItemId: uuidv4(),
      });

      dispatch({
        type: 'ADD_ITEM',
        payload: { item: menuItemOfBag },
      });

      function updateItemThatAlreadyExists() {
        const menuItemOfBag = MenuItemFactory.createMenuItemWithDetailsOfBag({
          menuItem: itemAlreadyExists!,
          amount: extra.amount + itemAlreadyExists!.amount,
          observations: extra.observations,
          bagItemId: itemAlreadyExists!.bagItemId,
        });

        dispatch({
          type: 'UPDATE_ITEM',
          payload: { bagItemIdForEdit: itemAlreadyExists!.bagItemId, newData: menuItemOfBag },
        });
      }
    },
    [items, updateItem]
  );

  const clearItems = useCallback(() => {
    dispatch({
      type: 'CLEAR_ITEMS',
    });
  }, []);

  const totalItemsPrice = useMemo(() => {
    return calculateTotalItemsPrice(items);
  }, [items]);

  return (
    <BagContext.Provider value={{ items, addItem, deleteItemByIndex, updateItem, totalItemsPrice, clearItems }}>
      {children}
    </BagContext.Provider>
  );
}

function itemsReducer(state = initialState, action: ItemsReducerAction<any>): MenuItemOfBag[] {
  switch (action.type) {
    case 'SET_ITEMS':
      const items = action.payload.items as MenuItemOfBag[];
      return [...items];
    case 'ADD_ITEM':
      const item = action.payload.item as MenuItemOfBag;
      return [...state, item];
    case 'UPDATE_ITEM':
      const bagItemIdForEdit = action.payload.bagItemIdForEdit as string;
      const newData = action.payload.newData as MenuItemOfBag;
      return state.map((item) => {
        if (bagItemIdForEdit === item.bagItemId) return newData;
        return item;
      });
    case 'DELETE_ITEM_BY_INDEX':
      const indexToDelete = action.payload.indexToDelete;
      return [...state.slice(0, indexToDelete), ...state.slice(indexToDelete + 1)];
    case 'CLEAR_ITEMS':
      return [];
    default:
      return state;
  }
}

const BagStorage = {
  save(cliqueiaAcheiId: string, items: MenuItemOfBag[]) {
    SessionStorage.setItem(`bag-items-${cliqueiaAcheiId}`, JSON.stringify(items));
  },
  get(cliqueiAcheiId: string) {
    const bagItemsAsString = SessionStorage.getItem(`bag-items-${cliqueiAcheiId}`);
    return (bagItemsAsString ? JSON.parse(bagItemsAsString) : []) as MenuItemOfBag[];
  },
};

type ItemsReducerAction<P> = {
  type: ItemActionsType;
  payload?: any;
};

type ItemActionsType = 'SET_ITEMS' | 'ADD_ITEM' | 'DELETE_ITEM_BY_INDEX' | 'CLEAR_ITEMS' | 'UPDATE_ITEM';

export function useBagContext() {
  return useContext(BagContext);
}
